import React, { useState } from 'react';
import {
  Paper,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  Divider,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { formatDate } from '../../utils/editorUtils';
import '../../styles/DocumentCard.css';

// Utility to clean HTML and decode entities from preview text
const cleanPreviewText = (html) => {
  if (!html) return '';
  
  // Create a temporary element to decode HTML entities
  const temp = document.createElement('div');
  temp.innerHTML = html;
  let text = temp.textContent || temp.innerText || '';
  
  // Remove extra whitespace and normalize spaces
  text = text.replace(/\s+/g, ' ').trim();
  
  return text;
};

const DocumentCard = ({ document, onClick, onDelete, onLinkCover, onUnlinkCover, onOpenStructure }) => {
  const handleClick = typeof onClick === 'function' ? onClick : undefined;
  const [anchorEl, setAnchorEl] = useState(null);
  const [coverFailed, setCoverFailed] = useState(false);
  const open = Boolean(anchorEl);

  const hasMenu = onDelete || onLinkCover || onUnlinkCover || onOpenStructure;

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (e) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(document._id);
    handleMenuClose(e);
  };

  const handleOpenStructure = (e) => {
    e.stopPropagation();
    onOpenStructure?.(document._id);
    handleMenuClose(e);
  };

  const handleLinkCover = (e) => {
    e.stopPropagation();
    onLinkCover?.(document._id);
    handleMenuClose(e);
  };

  const handleUnlinkCover = (e) => {
    e.stopPropagation();
    onUnlinkCover?.(document._id);
    handleMenuClose(e);
  };

  // A listing presigns these, so they expire; a dead URL falls back to the
  // placeholder rather than showing a broken image in a grid of cards.
  const coverUrl = !coverFailed ? (document.cover_url || '') : '';

  // Clean the preview text
  const cleanedPreview = cleanPreviewText(document.preview || '');
  const previewText = cleanedPreview.split(' ').slice(0, 10).join(' ') + 
                      (cleanedPreview.split(' ').length > 10 ? '...' : '');

  return (
    <Paper
      className="document-card"
      onClick={handleClick}
      sx={{
        p: 0,
        display: 'flex',
        flexDirection: 'row',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 6px 12px rgba(0,0,0,0.1)'
        }
      }}
    >
      {/* Cover strip. Full-bleed down the left edge, centre-cropped: a book
          cover is 2:3 and this card is landscape, so showing one whole would
          cost nearly half the width. Cropping the sides keeps the title block,
          which is the part that makes a cover recognisable this small. */}
      <Box className="document-card__cover" aria-hidden={!coverUrl}>
        {coverUrl ? (
          <>
          <img className="document-card__cover-bg" src={coverUrl} alt="" aria-hidden="true" />
          <img
            className="document-card__cover-art"
            src={coverUrl}
            alt={document.linkedCoverTitle ? `Cover: ${document.linkedCoverTitle}` : 'Book cover'}
            onError={() => setCoverFailed(true)}
          />
          </>
        ) : (
          <Box className="document-card__cover-empty">
            <MenuBookIcon fontSize="small" />
          </Box>
        )}
      </Box>

      <Box className="document-card__body">
      {document.reviewNotes && (
        <Tooltip title={document.reviewNotes} placement="top" arrow>
          <Chip
            label="Needs Revision"
            size="small"
            sx={{
              mb: 1,
              backgroundColor: '#fff3e0',
              color: '#e65100',
              fontWeight: 600,
              fontSize: '0.7rem',
              cursor: 'help',
            }}
          />
        </Tooltip>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          variant="subtitle1"
          component="h3"
          sx={{
            fontWeight: 600,
            // Two lines, not one: the cover strip narrowed this column, and a
            // single line ellipsed titles as short as "E2E — Cork Board".
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.25,
            wordBreak: 'break-word',
            color: '#413B8D',
          }}
        >
          {document.title}
        </Typography>
        {hasMenu && (
          <>
            <IconButton size="small" onClick={handleMenuOpen}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              onClick={(e) => e.stopPropagation()}
            >
              {onOpenStructure && (
                <MenuItem onClick={handleOpenStructure}>
                  <AutoStoriesIcon fontSize="small" sx={{ mr: 1 }} />
                  Manuscript Structure
                </MenuItem>
              )}
              {onOpenStructure && (onLinkCover || onUnlinkCover || onDelete) && <Divider />}
              {onLinkCover && (
                <MenuItem onClick={handleLinkCover}>
                  <LinkIcon fontSize="small" sx={{ mr: 1 }} />
                  {document.linkedCoverId ? 'Change Cover Link' : 'Link to Cover'}
                </MenuItem>
              )}
              {onUnlinkCover && document.linkedCoverId && (
                <MenuItem onClick={handleUnlinkCover}>
                  <LinkOffIcon fontSize="small" sx={{ mr: 1 }} />
                  Unlink Cover
                </MenuItem>
              )}
              {(onLinkCover || onUnlinkCover) && onDelete && <Divider />}
              {onDelete && <MenuItem onClick={handleDelete}>Delete</MenuItem>}
            </Menu>
          </>
        )}
      </Box>

      {document.linkedCoverTitle && (
        <Tooltip title={`Cover: ${document.linkedCoverTitle}`} placement="top">
          <Chip
            icon={<LinkIcon sx={{ fontSize: '0.75rem !important' }} />}
            label={document.linkedCoverTitle}
            size="small"
            sx={{
              mt: 0.5,
              mb: 0.5,
              maxWidth: '100%',
              fontSize: '0.65rem',
              height: 20,
              bgcolor: '#ede9fe',
              color: '#5b21b6',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        </Tooltip>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 0.5 }}
      >
        Last edited: {formatDate(document.lastEdited)}
      </Typography>

      {document.reviewNotes && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.75,
            px: 1,
            py: 0.5,
            borderLeft: '3px solid #e65100',
            color: '#bf360c',
            backgroundColor: '#fff8f5',
            borderRadius: '0 4px 4px 0',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {document.reviewNotes}
        </Typography>
      )}

      <Typography
        variant="body2"
        sx={{
          mt: 1.5,
          flexGrow: 1,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {previewText}
      </Typography>

      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 0.5,
        mt: 1.5,
      }}>
        {/* `nowrap` on both: the cover strip narrowed this column enough that
            "0 words" and "1 min read" were each breaking over two lines. */}
        <Typography variant="caption" color="primary" sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
          {document.wordCount ?? 0} words
        </Typography>
        <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
          {document.readingTime ?? 1} min read
        </Typography>
      </Box>
      </Box>
    </Paper>
  );
};

export default DocumentCard;
